import { useEffect, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useSearchParams } from 'react-router-dom';

import importImage from '../../assets/images/product-import.png';
import { Marketplaces } from '../../common/constants/enums';
import { getImportProductSchedulerApi } from '../../common/services/import-product-scheduler-api.service';
import { FormItem } from '../../common/ui/formItem';
import { Modal } from '../../common/ui/modal';
import { Select } from '../../common/ui/select';
import { useLocalStorage } from '../../hooks';
import { ImportPanel } from './components/import-panel';
import { MessagesModalContent } from './components/messages-modal-content';
import styles from './product-import-page.module.scss';
import { TAsinHistoryItem, TMessage } from './types';

interface RegionOption {
  label: React.ReactNode;
  value: string;
}

interface MarketplaceOption {
  label: string;
  value: Marketplaces;
}

const MARKETPLACE_OPTIONS: MarketplaceOption[] = [
  { label: 'Amazon', value: Marketplaces.Amazon },
  { label: 'Walmart', value: Marketplaces.Walmart },
];

const REGION_OPTIONS: RegionOption[] = [
  { label: 'United States', value: 'us' },
  { label: 'United Kingdom', value: 'uk' },
  { label: 'Germany', value: 'de' },
  { label: 'France', value: 'fr' },
  { label: 'Italy', value: 'it' },
  { label: 'Spain', value: 'es' },
  { label: 'Canada', value: 'ca' },
  { label: 'Japan', value: 'jp' },
  { label: 'Australia', value: 'au' },
];

const formatRegionOptionLabel = (option: RegionOption) => (
  <div style={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
    <ReactCountryFlag
      countryCode={option.value === 'uk' ? 'GB' : option.value.toUpperCase()}
      style={{ fontSize: '1.2em' }}
      svg
    />
    <span>{option.label}</span>
  </div>
);

const ProductImportPage = () => {
  const [params] = useSearchParams();
  const [messages, setMessages] = useState<TMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [asins, setAsins] = useState([]);
  const [asinsHistory, setAsinsHistory] = useLocalStorage<TAsinHistoryItem[]>('asinsHistory', []);
  const [inputValue, setInputValue] = useState('');
  const [selectedMarketplace, setSelectedMarketplace] = useState<MarketplaceOption>(MARKETPLACE_OPTIONS[0]);
  const [selectedRegion, setSelectedRegion] = useState<RegionOption>(REGION_OPTIONS[0]);

  const addProductToScheduler = async (asins: string[], region: string, marketplace: Marketplaces) => {
    setLoading(true);

    try {
      if (marketplace === Marketplaces.Amazon) {
        // Use v1 endpoint for US products, v2 for other regions
        if (region === 'us') {
          const response = await getImportProductSchedulerApi().addProductToScheduler({ asins });
          if (response.status === 201 && response.data.available.length) {
            const availableAsins = response.data.available.map((x) => x.asin);

            setMessages((prevState) => [
              ...prevState,
              {
                asins: availableAsins,
                message: 'The Asins already available in the database',
              },
            ]);
          }

          if (response.status === 201 && response.data.scheduled.length) {
            setMessages((prevState) => [
              ...prevState,
              {
                asins: response.data.scheduled,
                message: 'The Asins have been added to the queue',
              },
            ]);
          }
        } else {
          // Use v2 endpoint for non-US regions
          await getImportProductSchedulerApi().addProductToSchedulerV2({
            asins,
            region,
          });
          setMessages([{
            asins,
            message: 'The Asins have been scheduled for import',
          }]);
        }
      } else {
        await getImportProductSchedulerApi().addWalmartProducts({ w_items_id: asins });
        setMessages([{
          asins,
          message: 'The items have been scheduled for import',
        }]);
      }
    } catch (e) {
      setMessages([{ asins: [], message: 'Something wrong' }]);
    } finally {
      setAsinsHistory([
        ...asinsHistory,
        ...asins.map((asin) => ({ asin, date: new Date().toISOString() })),
      ]);
      setLoading(false);
    }
  };

  const inputHandler = (value: string, asins: string[]) => {
    setInputValue(value);
    setAsins(asins);
  };

  const onModalButtonClose = () => {
    setMessages([]);
    setAsins([]);
    setInputValue('');
  };

  const onSendClick = () => {
    const data = [...asins];
    if (selectedMarketplace.value === Marketplaces.Amazon && inputValue.length === 10) {
      data.push(inputValue);
    }
    if (selectedMarketplace.value === Marketplaces.Walmart && inputValue.trim().length) {
      data.push(inputValue.trim());
    }
    addProductToScheduler(data, selectedRegion.value, selectedMarketplace.value);
  };

  useEffect(() => {
    if (params.get('asin')) {
      setAsins([params.get('asin')]);
    }
    if (params.get('region')) {
      const region = REGION_OPTIONS.find((r) => r.value === params.get('region'));
      if (region) {
        setSelectedRegion(region);
      }
    }
    if (params.get('marketplace')) {
      const marketplace = MARKETPLACE_OPTIONS.find((item) => item.value === params.get('marketplace'));
      if (marketplace) {
        setSelectedMarketplace(marketplace);
      }
    }
  }, []);

  const isAmazonMarketplace = selectedMarketplace.value === Marketplaces.Amazon;
  const sendBtnDisabled = isAmazonMarketplace
    ? (!asins.length && inputValue.length !== 10)
    : (!asins.length && !inputValue.trim().length);
  const inputPlaceholder = isAmazonMarketplace ? 'Input ASINs' : 'Input Walmart product IDs';

  return (
    <div className={styles.product_scheduler}>
      <img alt="product-import-img" className={styles.logo} src={importImage} />
      <h1 className={styles.title}>Product import scheduler</h1>
      <span className={styles.hint}>
        {isAmazonMarketplace ? 'Send your ASINs in field below.' : 'Send your Walmart product IDs in field below.'}
      </span>
      <div className={styles.selects_row}>
        <FormItem className={styles.marketplace_select} label="Marketplace">
          <Select
            onChange={(option) => setSelectedMarketplace(option as MarketplaceOption)}
            options={MARKETPLACE_OPTIONS}
            value={selectedMarketplace}
          />
        </FormItem>
        {isAmazonMarketplace && (
          <FormItem className={styles.region_select} label="Region">
            <Select
              formatOptionLabel={formatRegionOptionLabel}
              onChange={(option) => setSelectedRegion(option as RegionOption)}
              options={REGION_OPTIONS}
              value={selectedRegion}
            />
          </FormItem>
        )}
      </div>
      <div className={styles.input_row}>
        <ImportPanel
          asins={asins}
          inputHandler={inputHandler}
          inputValue={inputValue}
          loading={loading}
          onSendClick={onSendClick}
          placeholder={inputPlaceholder}
          sendBtnDisabled={sendBtnDisabled}
        />
      </div>
      {!!messages.length && (
        <Modal open={!!messages.length}>
          <MessagesModalContent messages={messages} onModalButtonClose={onModalButtonClose} />
        </Modal>
      )}
    </div>
  );
};

export { ProductImportPage };
